import { RotateCcw, ArrowRight } from "lucide-react";
import { Avatar, AvatarFallback } from "@/components/ui/library/avatar";
import useLeadPipeline from "@/features/leads/hook/useLeadPipeline";
import { getInitials } from "@/utils/convetors";
import ActivityTimestamp from "./ActivityTimestamp";

interface StatusActivityProps {
  activity: any;
  isLast: boolean;
}

function resolveNames(pipeline: any[], metadata: any) {
  if (!pipeline?.length || !metadata) return { from: null, to: null };

  const findStage = (id: number) => pipeline.find((s: any) => s.id === id);
  const findStatus = (stage: any, id: number) =>
    stage?.statuses?.find((s: any) => s.id === id);
  const findSubStatus = (status: any, id: number) =>
    status?.subStatuses?.find((s: any) => s.id === id);

  const fromStage = findStage(metadata.fromStageId);
  const toStage = findStage(metadata.toStageId);

  const fromStatus = findStatus(fromStage, metadata.fromStatusId);
  const toStatus = findStatus(toStage, metadata.toStatusId);

  const fromSub = findSubStatus(fromStatus, metadata.fromSubStatusId);
  const toSub = findSubStatus(toStatus, metadata.toSubStatusId);

  const buildLabel = (stage: any, status: any, sub: any) => {
    const parts = [stage?.name, status?.name, sub?.name].filter(Boolean);
    return parts.length ? parts.join(" / ") : null;
  };

  return {
    from: buildLabel(fromStage, fromStatus, fromSub),
    to: buildLabel(toStage, toStatus, toSub),
  };
}

export default function StatusActivity({
  activity,
  isLast,
}: StatusActivityProps) {
  const { pipeline } = useLeadPipeline();

  const user = activity.user ?? {};
  const metadata = activity.metadata;
  const { from, to } = resolveNames(pipeline, metadata);


  return (
    <div className="flex gap-4 items-start relative w-full">
      <div className="flex flex-col items-center relative self-stretch shrink-0">
        <div className="bg-white border border-[#e6e8e7] flex items-center justify-center p-2 relative rounded-[12px] shadow-[0px_4px_9px_0px_rgba(0,0,0,0.07)] shrink-0 size-[36px]">
          <RotateCcw className="size-4 text-[#218760]" strokeWidth={2} />
        </div>
        {!isLast && (
          <div className="flex-1 min-h-[32px] w-0 relative shrink-0">
            <div className="absolute inset-0 border-l border-[#e6e8e7] border-dashed" />
          </div>
        )}
      </div>

      <div
        className={`flex-1 flex flex-col items-start justify-center min-h-0 ${
          isLast ? "pb-0" : "pb-8"
        } pt-[9px] px-0 relative min-w-0`}
      >
        <div className="flex items-start justify-between leading-normal relative shrink-0 w-full mb-0">
          <p className="text-[14px] font-medium text-black tracking-[-0.56px] leading-[1.35] flex-1 min-w-0 break-words [overflow-wrap:anywhere]">
            <span className="text-[#218760]">{user.name ?? "System"}</span>{" "}
            {activity.content}
          </p>
          <ActivityTimestamp createdAt={activity.createdAt} />
        </div>

        {(from || to) && (
          <div className="flex gap-2 items-center pb-0 pt-3 px-0 relative shrink-0 w-full flex-wrap">
            {from && (
              <div className="flex h-[22px] items-center justify-center overflow-hidden px-[10px] py-[2px] relative rounded-full shrink-0 bg-[#fce8e8]">
                <p className="text-[12px] font-medium leading-normal relative shrink-0 text-nowrap tracking-[-0.24px] text-[#7a2020]">
                  {from}
                </p>
              </div>
            )}
            <ArrowRight
              className="size-4 text-[#697571] shrink-0"
              strokeWidth={2}
            />
            {to && (
              <div className="flex h-[22px] items-center justify-center overflow-hidden px-[10px] py-[2px] relative rounded-full shrink-0 bg-[#d6ebe3]">
                <p className="text-[12px] font-medium leading-normal relative shrink-0 text-nowrap tracking-[-0.24px] text-[#1e302a]">
                  {to}
                </p>
              </div>
            )}
          </div>
        )}

        <div className="flex gap-1 items-center pb-0 pt-2 px-0 relative shrink-0">
          <Avatar className="size-5 shrink-0 rounded-full bg-[#e1e4ea]">
            <AvatarFallback className="text-[10px] font-medium leading-[16px] bg-[#e1e4ea] text-[#505e59]">
              {getInitials(user.name ?? "System")}
            </AvatarFallback>
          </Avatar>
          <p className="text-[12px] font-normal text-[#1e302a] tracking-[-0.24px] text-nowrap">
            {user.name ?? "System"}
          </p>
        </div>
      </div>
    </div>
  );
}
